import { Icon } from '@repo/design-system/components/ds/icon';
import { ProgressBar } from '@repo/design-system/components/ds/progress-bar';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { InfoBanner } from '@repo/design-system/components/ui/info-banner';
import { Type } from '@repo/design-system/components/ui/type';
import { useT } from '@repo/i18n/components/i18n.client';
import { css } from '@repo/panda/css';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import {
    selectActiveDevelopmentMessageDismissedAt,
    setActiveDevelopmentMessageDismissedAt,
} from '@repo/store/maps/slices/clientContext';
import { selectExternalMapId, selectFoundPins, selectGroupedCategories } from '@repo/store/maps/slices/mapData';
import {
    getJsonCookieRecordValue,
    isTimestampWithinDays,
    setJsonCookieRecordTimestamp,
} from '@repo/utility/json-cookie-record';
import type React from 'react';
import { useEffect, useState } from 'react';

const DISMISS_COOKIE_KEY = 'dismiss-active-development-message';
const DISMISS_DAYS = 7;

const bannerWrapperStyles = css.raw({
    width: 'full',
    overflow: 'hidden',
    borderRadius: '16',
    backdropFilter: '[blur(32px)]',
});

interface MapDiscoveryProgressProps {
    onClick?: () => void;
    foundCount?: number;
    totalCount?: number;
    showActiveDevelopmentMessage?: boolean;
}

function getActiveDevelopmentMessageDismissCookie(externalMapId: string | null | undefined): {
    mapId: string | null;
    dismissedAt: string | null;
} {
    return {
        mapId: externalMapId ?? null,
        dismissedAt: externalMapId ? getJsonCookieRecordValue(DISMISS_COOKIE_KEY, externalMapId) : null,
    };
}

export const MapDiscoveryProgress: React.FC<MapDiscoveryProgressProps> = ({
    onClick,
    foundCount,
    totalCount,
    showActiveDevelopmentMessage = false,
}) => {
    const dispatch = useAppDispatch();
    const isLoggedIn = useAppSelector(state => state.auth.isAuthenticated);
    const externalMapId = useAppSelector(selectExternalMapId);
    const activeDevelopmentMessageDismissedAt = useAppSelector(selectActiveDevelopmentMessageDismissedAt);
    const groupedCategories = useAppSelector(selectGroupedCategories);
    const foundPins = useAppSelector(selectFoundPins);
    const { t } = useT(['game_map']);
    const [activeDevelopmentMessageDismissCookie, setActiveDevelopmentMessageDismissCookie] = useState<{
        mapId: string | null;
        dismissedAt: string | null;
    }>(() => getActiveDevelopmentMessageDismissCookie(externalMapId));

    useEffect(() => {
        setActiveDevelopmentMessageDismissCookie(getActiveDevelopmentMessageDismissCookie(externalMapId));
    }, [externalMapId]);

    const totalLocations =
        totalCount ??
        groupedCategories?.reduce(
            (sum, category) => sum + category.subcategories.reduce((catSum, sub) => catSum + sub.locationCount, 0),
            0,
        ) ??
        0;

    const foundLocations = foundCount ?? Object.keys(foundPins ?? {}).filter(key => foundPins?.[key]).length;

    const handleClick = () => {
        onClick?.();
    };

    const handleDismissActiveDevelopmentMessage = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        if (!externalMapId) return;

        const dismissedAt = setJsonCookieRecordTimestamp(DISMISS_COOKIE_KEY, externalMapId, DISMISS_DAYS);
        setActiveDevelopmentMessageDismissCookie({ mapId: externalMapId, dismissedAt });

        if (isLoggedIn) {
            dispatch(setActiveDevelopmentMessageDismissedAt(dismissedAt));
        }
    };

    const isActiveDevelopmentMessageDismissedByPreference =
        isLoggedIn && isTimestampWithinDays(activeDevelopmentMessageDismissedAt, DISMISS_DAYS);

    const isActiveDevelopmentMessageDismissCookieReady =
        activeDevelopmentMessageDismissCookie.mapId === (externalMapId ?? null);
    const isActiveDevelopmentMessageDismissedByCookie =
        isActiveDevelopmentMessageDismissCookieReady &&
        isTimestampWithinDays(activeDevelopmentMessageDismissCookie.dismissedAt, DISMISS_DAYS);

    const shouldShowActiveDevelopmentMessage =
        showActiveDevelopmentMessage &&
        !!externalMapId &&
        isActiveDevelopmentMessageDismissCookieReady &&
        !isActiveDevelopmentMessageDismissedByCookie &&
        !isActiveDevelopmentMessageDismissedByPreference;

    return (
        <FlexRow align="center" width="full" onClick={handleClick} className={bannerWrapperStyles}>
            <FlexColumn
                width="full"
                className={css.raw({
                    borderRadius: '[16px]',
                    overflow: 'hidden',
                    backgroundColor: 'yellow.500/10',
                })}
            >
                <InfoBanner
                    interactive={!!onClick}
                    icon="editor_choice"
                    variant="gamify"
                    borderRadius="none"
                    className={css({ borderRadius: shouldShowActiveDevelopmentMessage ? '[16px 16px 0 0]' : '16' })}
                >
                    <FlexColumn width="full" gap={isLoggedIn ? '4' : 'none'}>
                        <FlexRow width="full" justify="between">
                            <Type variant="caption" color="green.400">
                                {t($ => $.discovery_progress)}
                            </Type>
                            <Type variant="caption" color="text.primary">
                                {foundLocations} / {totalLocations}
                            </Type>
                        </FlexRow>
                        {!isLoggedIn && (
                            <Type variant="body-xs" color="text.primary">
                                {t($ => $.sign_in_notice)}
                            </Type>
                        )}

                        {isLoggedIn && <ProgressBar value={foundLocations} max={totalLocations} variant="gamify" />}
                    </FlexColumn>
                </InfoBanner>
                {shouldShowActiveDevelopmentMessage && (
                    <FlexRow
                        gap="12"
                        align="center"
                        className={css.raw({
                            paddingX: '12',
                            paddingY: '12',
                            backgroundColor: 'yellow.500/0',
                        })}
                        onClick={event => event.stopPropagation()}
                    >
                        <Icon fill icon="info" size="md" decorative className={css({ color: 'yellow.400' })} />
                        <Type variant="body-xs" color="yellow.100">
                            {t($ => $.game_map_details.map_being_worked_on)}
                            <button
                                type="button"
                                onClick={handleDismissActiveDevelopmentMessage}
                                className={css({
                                    color: 'yellow.400',
                                    fontWeight: 'bold',
                                    marginLeft: '4',
                                    cursor: 'pointer',
                                    background: 'transparent',
                                    border: 'none',
                                    padding: '0',
                                    font: 'inherit',
                                    _hover: {
                                        textDecoration: 'underline',
                                    },
                                })}
                            >
                                {t($ => $.dismiss)}
                            </button>
                        </Type>
                    </FlexRow>
                )}
            </FlexColumn>
        </FlexRow>
    );
};
